import { Controller } from "@hotwired/stimulus"

/**
 * Search Params Controller
 *
 * Attaches to a form (e.g. the filter modal) and ensures that search params
 * from the current URL are carried forward when the form submits.
 *
 * This solves the problem of two separate forms (navbar search bar + filter
 * modal) losing each other's params on submission. The filter form "owns"
 * its own inputs (price, amenities, etc.) and "carries" the search params
 * (destination, dates, guests) from the URL.
 *
 * By calling preventDefault ourselves and navigating via window.location,
 * we also sidestep the popup controller's hide() method which would
 * otherwise block the native form submit.
 */

// Search params managed by the navbar — carried into the filter form from URL
const CARRY_PARAMS = [
  "search",
  "destination",
  "query",
  "lat",
  "lng",
  "arrival_date",
  "departure_date",
  "adults",
  "children",
  "infants",
  "pets",
]

export default class extends Controller {
  private boundHandleSubmit: (event: Event) => void

  connect(): void {
    this.boundHandleSubmit = this.handleSubmit.bind(this)
    this.element.addEventListener("submit", this.boundHandleSubmit)
  }

  disconnect(): void {
    this.element.removeEventListener("submit", this.boundHandleSubmit)
  }

  handleSubmit(event: Event): void {
    event.preventDefault()

    const form = this.element as HTMLFormElement
    const formData = new FormData(form)

    // Collect param names the form already provides
    const formParamNames = new Set<string>()
    for (const [key] of formData) {
      formParamNames.add(key)
    }

    // Carry search params from URL that the form doesn't have
    const urlParams = new URLSearchParams(window.location.search)
    for (const param of CARRY_PARAMS) {
      if (!formParamNames.has(param)) {
        const values = urlParams.getAll(param)
        for (const v of values) {
          if (v && v !== "None") {
            formData.append(param, v)
          }
        }
      }
    }

    // Build clean URL — strip empty/null values
    const params = new URLSearchParams()
    for (const [key, value] of formData) {
      const strValue = String(value)
      if (strValue !== "" && strValue !== "None" && strValue !== "undefined") {
        params.append(key, strValue)
      }
    }

    const action = form.getAttribute("action") || window.location.pathname
    window.location.href = `${action}?${params.toString()}`
  }
}
